/* eslint-disable no-param-reassign, no-unused-vars */

import axios from 'axios';
import GameModel from 'Models/GameModel';
import AppModel from 'Models/AppModel';
import googleTagAnalytics from 'Root/drivers/googleTagAnalytics';
import { makeTagPathArray, makeTagPathArrayByParent } from 'Root/helpers/tools';
import { cleanProduct } from 'Root/helpers/product';
import { mutateEntity } from 'Root/store/components/commonMutations';
import { flatTagsById, setFlatTagsById, setRootTagsByGames } from 'Root/helpers/tags';

const { API_URI } = AppModel;

function getTagIndex(a, v) {
  if (a[0].priority < v.priority) {
    return 0;
  }

  if (a[0].priority === v.priority && a[0].title < v.title) {
    return 0;
  }

  let i = 1;
  while (i < a.length) {
    if ((a[i].priority < v.priority && a[i - 1].priority >= v.priority)) {
      return i;
    }

    if (a[i].priority === v.priority && a[i].title > v.title) {
      return i;
    }

    i += 1;
  }

  return i;
}

function setTagData(tagData, tagUrl) {
  return {
    ...tagData,
    _isLongTitle: tagData.title.length > 14,
    _categories: tagUrl,
    _href: `/${GameModel.ID_TO_DATA[tagData.game_id].routeNamespace}-boost/${tagUrl.join('/')}`,
  };
}
function setAdditionInfoToTag(tagData) {
  const tagUrl = makeTagPathArrayByParent(tagData);
  return setTagData(tagData, tagUrl);
}

export default {
  namespaced: true,

  state: () => ({
    products: [],
    headerInfo: {},
    product: null,
    productOffers: {
      data: [],
      total: null,
    },
    accountOffers: {
      params: {},
      data: [],
      total: null,
    },
    accountOffer: null,
    // productsPrefetched: false,
    tags: [],
    productRaids: null,
    activeTag: {},
    totalResults: null,
    todayTop: {},
    followUsImages: [],
    popularHeaderLinks: [],
    accountsPresets: null,
    pinnedOffers: [],
  }),

  getters: {
    breadcrumbs(state, getters, rootState) {
      const { activeGame } = rootState;

      const items = [
        {
          title: 'SkyCoach',
          href: '/',
        },
        {
          title: activeGame.fullTitle,
          href: `/${activeGame.routeNamespace}-boost`,
        },
      ];

      if (state.activeTag && state.tags.length) {
        let tag = state.activeTag;
        const initialTag = tag;

        while (tag.parent_id !== null) {
          tag = flatTagsById[tag.parent_id];
          items.push({
            id: tag.id,
            title: tag.title,
            href: tag._href,
          });
        }

        items.push({
          id: initialTag.id,
          title: initialTag.title,
          href: initialTag._href,
        });
      }

      if (state.product) {
        const title = state.product.seo_title ?? state.product.title;
        const productSlug = state.product.title_slug ?? state.product.id;
        items.push({
          title,
          href: `/${activeGame.routeNamespace}-boost/products/${productSlug}`,
        });
      }

      return items;
    },

    // gameById: (state) => (gameId) => state.games.find((game) => +game.id === +gameId),

    productById: (state) => (productId) => state.products.find((product) => +product.id === +productId),

    // productsFilteredByGame(state) {
    //   return state.products;
    // },

    filterFields(state, getters, rootState) {
      if (!state.product?.product_currency_setting?.product_currency_setting_fields) {
        return null;
      }

      // матчим регион с регионом в фильтре при заходе в товар
      // переключаем на регион из шапки если он отличается от дефолта
      if (
        +state.product.game === GameModel.WOW.id
        || +state.product.game === GameModel.WOW_WOTLK.id
        || +state.product.game === GameModel.WOW_CATACLYSM.id
      ) {
        const regionField = state.product.product_currency_setting.product_currency_setting_fields
          .find((field) => field.title === 'Region');

        if (regionField?.default_value_id) {
          let regionDefaultValue;
          let activeRegionValue;

          regionField.product_currency_setting_field_values
            .forEach((value) => {
              if (value.id === regionField.default_value_id) {
                regionDefaultValue = value;
              }

              if (
                rootState.activeRegion.regionCode === value.value
                || rootState.activeRegion.shortTitle === value.value
                || rootState.activeRegion.title === value.value
              ) {
                activeRegionValue = value;
              }
            });

          if (regionDefaultValue.id !== activeRegionValue.id) {
            regionField.default_value_id = activeRegionValue.id;
          }
        }
      }

      return state.product.product_currency_setting;
    },
  },

  actions: {
    getData({ state, rootState, dispatch }) {
      const tagsPromise = dispatch('getTags', false);

      // todo @GB-598 think about this
      return Promise.all([tagsPromise]);
    },

    getProductById({ commit }, {
      id, isOfferPreview, cookie, excludeDisabledOptions = true,
    }) {
      const params = {
        is_product_currency_setting_preview: isOfferPreview,
        exclude_disabled_options: excludeDisabledOptions,
      };

      return axios.get(`${API_URI}/product/${id}`, {
        params,
        ...(cookie ? { headers: { cookie } } : {}),
      });
    },

    getTags({ commit, rootState, state }, byGame = true) {
      let options = {};
      if (byGame) {
        options = rootState.activeGame ? { params: { game_id: rootState.activeGame.id } } : {};
      }
      return axios.get(`${API_URI}/tag/list-all`, options)
        .then(({ data }) => {
          commit('mutateTags', data.result);
          setFlatTagsById(state.tags);
          setRootTagsByGames();
        });
    },

    getTodayTop({ commit }, gameId) {
      return axios.get(`${API_URI}/product/list/badge-products?game_id=${gameId}`)
        .then(({ data }) => {
          if (Array.isArray(data)) {
            const productsByBadge = {};

            data.forEach((p) => {
              if (!productsByBadge[p.badge_id]) {
                productsByBadge[p.badge_id] = [];
              }
              productsByBadge[p.badge_id].push(p);
            });

            commit('mutateEntity', {
              entity: 'todayTop',
              data: { offers: productsByBadge, _gameId: gameId },
            });
          }
        });
    },

    async getFollowUsImages({ commit }) {
      const { data } = await axios.get(`${API_URI}/inst/get_images`);
      commit('mutateEntity', { entity: 'followUsImages', data });
    },

    async getCurrencyOffersAction({ commit }, { id, params, cancelToken }) {
      return axios.get(`${API_URI}/currency_marketplace/offers/${id}/list`, {
        cancelToken,
        params,
      }).then(({ data }) => {
        commit('mutateProductOffers', { offers: data.result, total: data.total });
      }).catch((err) => {
        console.error('Error take game offer currency', err);
      });
    },

    async getAccountOffersAction({ commit }, { params, cancelToken }) {
      try {
        const { data } = await axios.post(`${API_URI}/currency_marketplace/search`, params, {
          cancelToken,
        });

        let pinnedOffers = [];

        const shouldFetchPinnedOffers = params.page === 1
          && Object.keys(params.filters || {}).length === 0
          && !params.q
          && !params.price_range
          && !params.preset_id;

        if (shouldFetchPinnedOffers) {
          try {
            const pinnedOffersEnv = process.env.ACCOUNTS_PINNED_OFFERS;
            if (pinnedOffersEnv) {
              const pinnedOffersConfig = JSON.parse(pinnedOffersEnv);
              const pinnedOfferIds = pinnedOffersConfig[params.product_currency_setting_id];

              if (pinnedOfferIds && pinnedOfferIds.length > 0) {
                const pinnedResponse = await axios.post(
                  `${API_URI}/currency_marketplace/search`,
                  {
                    product_currency_setting_id: params.product_currency_setting_id,
                    offer_ids: pinnedOfferIds,
                  },
                  cancelToken,
                );

                if (pinnedResponse.data.data && pinnedResponse.data.data.length > 0) {
                  pinnedOffers = pinnedResponse.data.data;
                }
              }
            }
          } catch (pinnedError) {
            console.error('Error fetching pinned offers:', pinnedError);
          }
        } else {
          pinnedOffers = [];
        }

        commit('mutatePinnedOffers', pinnedOffers);
        commit('mutateAccountOffers', { offers: data.data, total: data.totalHits, params });
      } catch (err) {
        console.error('Error fetching account offers:', err);
      }
    },

    async getAccountOfferAction({ commit }, { currencySettingId, offerId, isPreview = false }) {
      try {
        const params = {
          with_id: offerId,
        };

        if (isPreview) {
          params.is_inactive = true;
        }

        const { data } = await axios
          .get(`${API_URI}/currency_marketplace/offers/${currencySettingId}/list`, {
            params,
          });
        if (data.result && data.result.length > 0) {
          commit('mutateAccountOffer', data.result[0]);
        } else {
          commit('mutateAccountOffer', null);
        }
        return data.result;
      } catch (err) {
        console.error('Error fetching account offer:', err);
        commit('mutateAccountOffer', null);
        return null;
      }
    },

    async fetchProductRaids({ commit, state }) {
      if (!state.product) {
        console.error('No product loaded to fetch raids for');
        return;
      }
      try {
        const response = await axios.get(`${API_URI}/raid_events/available`, {
          params: { with_product_id: state.product.id },
        });
        commit('setProductRaids', response.data.result);
      } catch (error) {
        console.error('Error fetching product raids:', error);
        commit('setProductRaids', null);
      }
    },

    async getAccountsPresetsAction({ commit }, { currencySettingId, cancelToken }) {
      try {
        const { data } = await axios.get(`${API_URI}/currency_marketplace/filter_presets/${currencySettingId}`, {
          cancelToken,
        });
        commit('mutateAccountsPresets', data);
      } catch (err) {
        console.error('Error fetching account presets:', err);
      }
    },
  },

  mutations: {
    mutateEntity,

    mutateTags(state, tags) {
      if (tags?.length > 0) {
        const newTags = [];
        let tagItem;

        tags.forEach((tag) => {
          const tagUrl = makeTagPathArray(tag, tags);

          tagItem = setTagData(tag, tagUrl);

          if (!newTags.length) {
            newTags.push(tagItem);
            return;
          }

          newTags.splice(getTagIndex(newTags, tagItem), 0, tagItem);
        });

        state.tags = newTags;
      } else {
        // default fallback in case of wrong\empty data
        state.tags = [];
      }
    },

    mutateActiveTag(state, tagId) {
      const tag = state.tags.find((tagItem) => tagItem.id === tagId);
      if (tag) {
        state.activeTag = tag;
        googleTagAnalytics.emitChooseProductCategory(tag.title);
      } else {
        state.activeTag = null;
      }
    },

    setActiveTag(state, tagData) {
      state.activeTag = setAdditionInfoToTag(tagData);
    },

    mutateProduct(state, product) {
      state.product = product ? cleanProduct(product) : null;
    },

    mutateProductOffers(state, { offers, total }) {
      state.productOffers.data = offers;
      state.productOffers.total = total;
    },

    mutateAccountOffers(state, { offers, total, params }) {
      state.accountOffers.data = offers;
      state.accountOffers.total = total;
      state.accountOffers.params = params;
    },

    mutateAccountOffer(state, offer) {
      state.accountOffer = offer;
    },

    setProductRaids(state, raids) {
      state.productRaids = raids;
    },

    mutatePinnedOffers(state, offers) {
      state.pinnedOffers = offers;
    },

    mutateAccountsPresets(state, presets) {
      state.accountsPresets = presets;
    },
  },
};
